import React, { useState, useEffect } from 'react';
import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import EmailSubscription from '../../components/email/EmailSubscription';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const ContactPage = () => {

  const [siteSettings, setSiteSettings] = useState({
    address: '123 Eagle Way, East End, NY 12345',
    phone: '(555) 123-4567',
    email: 'info@eastendprep.edu'
  });
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/site-settings`);
      if (response.ok) {
        const data = await response.json();
        setSiteSettings({
          address: data.address || '123 Eagle Way, East End, NY 12345',
          phone: data.phone || '(555) 123-4567',
          email: data.email || 'info@eastendprep.edu'
        });
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };





  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="heading-lg text-white mb-4">Contact Us</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Get in touch with East End Prep. We're here to answer your questions 
            about our athletics programs, admissions, and school community.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-2xl mx-auto">
          {/* School Information */}
          <div className="card">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">School Information</h2>
              
              <div className="space-y-4">
                {settingsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start space-x-3">
                      <MapPinIcon className="w-6 h-6 text-primary mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900">Address</h3>
                        <p className="text-gray-600" style={{whiteSpace: 'pre-line'}}>
                          {siteSettings.address}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <PhoneIcon className="w-6 h-6 text-primary mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900">Phone</h3>
                        <p className="text-gray-600">{siteSettings.phone}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <EnvelopeIcon className="w-6 h-6 text-primary mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900">Email</h3>
                        <p className="text-gray-600">{siteSettings.email}</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <section className="bg-white py-12 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="heading-md text-gray-900 mb-4">Visit Our Campus</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Located in the heart of East End, our campus features state-of-the-art 
              athletic facilities and modern classrooms designed for student success.
            </p>
          </div>
          
          {/* Placeholder for map - In a real application, you would integrate with Google Maps or similar */}
          <div className="bg-gray-200 rounded-lg h-96 flex items-center justify-center">
            <div className="text-center">
              <MapPinIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Interactive Map</h3>
              <p className="text-gray-600 mb-4">
                123 Eagle Way, East End, NY 12345
              </p>
              <a 
                href="https://maps.google.com/?q=123+Eagle+Way+East+End+NY+12345"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center"
              >
                <MapPinIcon className="w-4 h-4 mr-2" />
                View on Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Email Subscription Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Stay Connected
            </h2>
            <p className="text-lg text-gray-600">
              Subscribe to receive updates about events, news, and important announcements.
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            <EmailSubscription />
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="bg-secondary py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-md text-white mb-8">Quick Links</h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a 
              href="/sports" 
              className="bg-white bg-opacity-10 hover:bg-opacity-20 text-white p-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <UserIcon className="w-5 h-5" />
              <span>Sports Programs</span>
            </a>
            
            <a 
              href="/events" 
              className="bg-white bg-opacity-10 hover:bg-opacity-20 text-white p-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <CalendarIcon className="w-5 h-5" />
              <span>Upcoming Events</span>
            </a>
            
            <a 
              href="/news" 
              className="bg-white bg-opacity-10 hover:bg-opacity-20 text-white p-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <EnvelopeIcon className="w-5 h-5" />
              <span>Latest News</span>
            </a>
            
            <a 
              href="/media" 
              className="bg-white bg-opacity-10 hover:bg-opacity-20 text-white p-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <UserIcon className="w-5 h-5" />
              <span>Media Gallery</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;